import React, { useState, useEffect } from 'react';
import { Typography, message } from 'antd';
import { ExclamationCircleOutlined, LogoutOutlined, ReloadOutlined } from '@ant-design/icons';
import { getAccountCheck, getPopupList, confirmAnnouncement } from 'api/announcement';
import { IAccountCheckResponse, IPopupListResponse } from 'api/announcement';
import BaseModal from './BaseModal';
import StatusModal, { StatusModalButton } from './StatusModal';
import ContentModal, { CONTENT_BADGES } from './ContentModal';

const { Text } = Typography;

interface IAnnouncementPopupProps {
  onTriggerLogout: () => void; // Function to trigger logout
  onComplete: () => void; // Function called when all popups are complete
}

// Modal types for different phases
type ModalPhase =
  | 'checking'
  | 'logout_required'
  | 'logout_modal'
  | 'consent_modal'
  | 'info_modal'
  | 'complete';

const AnnouncementPopup: React.FC<IAnnouncementPopupProps> = ({ onTriggerLogout, onComplete }) => {
  // State management for the flow
  const [phase, setPhase] = useState<ModalPhase>('checking');
  const [accountInfo, setAccountInfo] = useState<(IAccountCheckResponse & { supplier_email: string }) | null>(null);
  const [popupData, setPopupData] = useState<IPopupListResponse | null>(null);
  const [currentConsentIndex, setCurrentConsentIndex] = useState(0);
  const [currentInfoIndex, setCurrentInfoIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize the popup flow on component mount
  useEffect(() => {
    initializePopupFlow();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initializePopupFlow = async () => {
    try {
      setPhase('checking');
      setError(null);

      // Step 1: Check account status
      const accountResponse = await getAccountCheck();
      setAccountInfo(accountResponse);

      // Step 2: Authentication check
      if (!accountResponse.is_main_account && !accountResponse.is_main_account_consented) {
        // Sub account + main account not consented → Show logout modal immediately
        setPhase('logout_modal');
        return;
      }

      // Step 3: Load popup lists
      const popupResponse = await getPopupList();
      setPopupData(popupResponse);

      // Step 4: Determine next phase
      if (popupResponse.popup_consent.length > 0) {
        setPhase('consent_modal');
      } else if (popupResponse.popup_info.length > 0) {
        setPhase('info_modal');
      } else {
        setPhase('complete');
        onComplete();
      }
    } catch (error) {
      console.error('Failed to initialize popup flow:', error);
      setError('載入公告時發生錯誤，請重新整理頁面');
      message.error('載入公告失敗');
    }
  };

  const handleConsentConfirmation = async () => {
    if (!popupData || !popupData.popup_consent[currentConsentIndex]) return;

    const announcement = popupData.popup_consent[currentConsentIndex];
    setIsProcessing(true);

    try {
      await confirmAnnouncement(announcement.id);

      // Move to next consent announcement
      const nextIndex = currentConsentIndex + 1;
      if (nextIndex < popupData.popup_consent.length) {
        setCurrentConsentIndex(nextIndex);
      } else {
        // All consent items confirmed, move to info modal or complete
        if (popupData.popup_info.length > 0) {
          setPhase('info_modal');
          setCurrentInfoIndex(0);
        } else {
          setPhase('complete');
          onComplete();
        }
      }
    } catch (error) {
      console.error('Error confirming announcement:', error);
      message.error('確認公告失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConsentLogout = () => {
    // Main account doesn't agree to some announcements → Show logout
    onTriggerLogout();
  };

  const handleInfoNext = async () => {
    if (!popupData || !popupData.popup_info[currentInfoIndex]) return;

    const announcement = popupData.popup_info[currentInfoIndex];

    // Optional: Record viewing (can be skipped)
    try {
      await confirmAnnouncement(announcement.id);
    } catch (error) {
      console.error('Error recording info view:', error);
      // Don't show error - this is optional
    }

    // Move to next info announcement
    const nextIndex = currentInfoIndex + 1;
    if (nextIndex < popupData.popup_info.length) {
      setCurrentInfoIndex(nextIndex);
    } else {
      // All done
      setPhase('complete');
      onComplete();
    }
  };

  const handleInfoSkip = () => {
    // Skip current info announcement
    const nextIndex = currentInfoIndex + 1;
    if (popupData && nextIndex < popupData.popup_info.length) {
      setCurrentInfoIndex(nextIndex);
    } else {
      // All done
      setPhase('complete');
      onComplete();
    }
  };

  const handleLogoutModalClose = () => {
    onTriggerLogout();
  };

  // Helper function to get modal title with progress
  const getModalTitle = () => {
    switch (phase) {
      case 'checking':
        return '載入中...';
      case 'logout_modal':
        return '系統通知';
      case 'consent_modal':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>
              重要公告確認
              {popupData?.popup_consent && popupData.popup_consent.length > 1 && (
                <Text type="secondary" style={{ marginLeft: '8px', fontSize: '14px' }}>
                  ({currentConsentIndex + 1}/{popupData.popup_consent.length})
                </Text>
              )}
            </span>
          </div>
        );
      case 'info_modal':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
            <span>
              系統公告
              {popupData?.popup_info && popupData.popup_info.length > 1 && (
                <Text type="secondary" style={{ marginLeft: '8px', fontSize: '14px' }}>
                  ({currentInfoIndex + 1}/{popupData.popup_info.length})
                </Text>
              )}
            </span>
          </div>
        );
      default:
        return '系統公告';
    }
  };

  // Don't render if complete or error state
  if (phase === 'complete') {
    return null;
  }

  if (error) {
    return (
      <BaseModal open={true} title="錯誤">
        <StatusModal
          type="error"
          icon={<ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />}
          title="載入公告時發生錯誤"
          description={error}
          buttons={[
            {
              key: 'reload',
              type: 'primary',
              icon: <ReloadOutlined />,
              children: '重新整理頁面',
              onClick: () => window.location.reload(),
            },
          ]}
        />
      </BaseModal>
    );
  }

  // Render methods using new components
  const renderContent = () => {
    switch (phase) {
      case 'checking':
        return <StatusModal type="loading" description="正在載入公告..." padding="40px" />;

      case 'logout_modal':
        return (
          <StatusModal
            type="warning"
            icon={<ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />}
            title="需要主帳號確認"
            description={
              <>
                主帳號 ({accountInfo?.supplier_email}) 必須先確認公告，您才能繼續使用系統。
                <br />
                請聯絡主帳號持有者或登出系統。
              </>
            }
            buttons={[
              {
                key: 'logout',
                type: 'primary',
                icon: <LogoutOutlined />,
                children: '登出系統',
                onClick: handleLogoutModalClose,
              },
            ]}
          />
        );

      case 'consent_modal': {
        if (!popupData?.popup_consent[currentConsentIndex]) return null;

        const announcement = popupData.popup_consent[currentConsentIndex];

        // Dynamic confirm button text based on position
        const getConfirmButtonText = () => {
          if (popupData.popup_consent.length === 1) {
            return '確認並開始使用系統';
          }
          return currentConsentIndex === popupData.popup_consent.length - 1
            ? '確認並開始使用系統'
            : '確認並繼續下一則';
        };

        // Button order: Logout (left) → Confirm (right)
        const buttons: StatusModalButton[] = [
          {
            key: 'logout',
            danger: true,
            icon: <LogoutOutlined />,
            children: '不同意，登出系統',
            onClick: async () => {
              handleConsentLogout();
            },
          },
          {
            key: 'confirm',
            type: 'primary',
            children: getConfirmButtonText(),
            onClick: handleConsentConfirmation,
            loading: isProcessing,
          },
        ];

        return (
          <ContentModal
            badge={CONTENT_BADGES.consent}
            title={announcement.title}
            content={announcement.content}
            buttons={buttons}
            progress={{
              current: currentConsentIndex + 1,
              total: popupData.popup_consent.length,
            }}
          />
        );
      }

      case 'info_modal': {
        if (!popupData?.popup_info[currentInfoIndex]) return null;

        const announcement = popupData.popup_info[currentInfoIndex];

        return (
          <ContentModal
            badge={CONTENT_BADGES.info}
            title={announcement.title}
            content={announcement.content}
            buttons={[
              // Button order: Skip (left) → Next (right)
              {
                key: 'skip',
                children: '跳過',
                onClick: handleInfoSkip,
              },
              {
                key: 'next',
                type: 'primary',
                children: '知道了',
                onClick: handleInfoNext,
              },
            ]}
            progress={{
              current: currentInfoIndex + 1,
              total: popupData.popup_info.length,
            }}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <BaseModal
      open={
        phase === 'checking' ||
        phase === 'logout_modal' ||
        phase === 'consent_modal' ||
        phase === 'info_modal'
      }
      title={getModalTitle()}
      onCancel={phase === 'info_modal' ? () => setPhase('complete') : undefined}
      closable={phase === 'info_modal'}
      maskClosable={phase === 'info_modal'}
    >
      {renderContent()}
    </BaseModal>
  );
};

export default AnnouncementPopup;
